import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import PhysicalSample from '../../../../../types/physical-sample/PhysicalSample';
import PhysicalSampleEligibilityDiagnostics
    from '../../../../../types/physical-sample/PhysicalSampleEligibilityDiagnostics';
import PhysicalSampleFirmwareUpgradeDTO from '../../../../../types/physical-sample/PhysicalSampleFirmwareUpgradeDTO';
import SampleFirmwareRevision from '../../../../../types/sample-firmware-revision/SampleFirmwareRevision';
import SampleFirmwareUpgradeRequest from '../../../../../types/sample-firmware-upgrade/SampleFirmwareUpgradeRequest';
import Studio from '../../../../../types/studio/Studio';
import { PhysicalSampleService } from '../../../services/physical-sample.service';
import { SampleFirmwareRevisionService } from '../../../services/sample-firmware-revision.service';
import { buildStudioUpgradeTargets, resolveTargetStudios } from './firmware-upgrade-scope.util';

/** Component-provided (not root): declare it in the host component's `providers` array so each host gets its own isolated instance/state. */
@Injectable()
export class FirmwareUpgradeTriggerService {

    public physicalSampleChooserMode = false;
    public manualSelectionMode = false;
    public targetStudios: Studio[] = [];
    public chosenPhysicalSamples: PhysicalSample[] = [];
    public eligiblePhysicalSamplesForUpgrade: PhysicalSample[] = [];
    public loadingEligiblePhysicalSamples = false;
    public eligibilityDiagnostics: PhysicalSampleEligibilityDiagnostics | null = null;
    public loadingEligibilityDiagnostics = false;

    public chosenSampleFirmwareRevision: SampleFirmwareRevision = new SampleFirmwareRevision({});
    public immediateFirmwareUpgrade = false;
    public upgradeFirmwareInProgress = false;

    constructor(
        private physicalSampleService: PhysicalSampleService,
        private sampleFirmwareRevisionService: SampleFirmwareRevisionService,
        private messageService: MessageService
    ) {
    }

    public get targetStudioNames(): string {
        return this.targetStudios.map(studio => studio.name).filter(Boolean).join(', ');
    }

    /**
     * Distinguishes "no samples exist" / "already up to date" from an unmatched pcb mapping so a
     * mismatched HardwareRevision.pcbVersion is surfaced rather than looking like an empty studio.
     *
     * @return The message to display for a 0-eligible-samples result.
     */
    public get emptyEligibleSamplesMessage(): string {
        if (this.loadingEligibilityDiagnostics) {
            return 'Checking why no samples are eligible...';
        }
        if (!this.eligibilityDiagnostics) {
            return 'No eligible samples found for the selected scope.';
        }
        if (this.eligibilityDiagnostics.totalSamplesInStudio === 0) {
            return 'No physical samples exist in the selected scope.';
        }
        if (this.eligibilityDiagnostics.unmatchedPcbValues.length > 0) {
            const rawValues = this.eligibilityDiagnostics.unmatchedPcbValues
                .map(value => `'${ value ?? 'unknown' }'`).join(', ');
            return `0 eligible samples - the reported PCB value(s) don't match this firmware's `
                + `compatible hardware revisions: ${ rawValues }.`;
        }
        return 'All samples in the selected scope are already on this firmware version.';
    }

    private get selectedStudioNamesFromChosenSamples(): string {
        return Array.from(new Set(this.chosenPhysicalSamples.map(sample => sample.studio?.name).filter(Boolean)))
            .join(', ');
    }

    /**
     * Studio/Country/Global scope defaults to "upgrade every eligible sample"; a Sample-scope
     * selection (Test Install/Re-Install) opens straight into manual per-sample selection.
     *
     * @param physicalSampleFirmwareUpgradeDTO The chosen scope and target firmware revision.
     * @param allAvailableStudios Every known studio, used to resolve Country/Global scope.
     */
    public choosePhysicalSampleForFirmwareUpgrade(
        physicalSampleFirmwareUpgradeDTO: PhysicalSampleFirmwareUpgradeDTO,
        allAvailableStudios: Studio[]
    ): void {
        if (physicalSampleFirmwareUpgradeDTO.compatibleFirmwareRevision) {
            this.chosenSampleFirmwareRevision = physicalSampleFirmwareUpgradeDTO.compatibleFirmwareRevision;
        }
        this.immediateFirmwareUpgrade = physicalSampleFirmwareUpgradeDTO.immediate;
        this.targetStudios = resolveTargetStudios(physicalSampleFirmwareUpgradeDTO, allAvailableStudios);
        this.manualSelectionMode = !physicalSampleFirmwareUpgradeDTO.preSelectAll;

        this.physicalSampleChooserMode = true;
        this.chosenPhysicalSamples = [];
        this.loadEligiblePhysicalSamplesForUpgrade(physicalSampleFirmwareUpgradeDTO.preSelectAll);
    }

    public enableManualSelection(): void {
        this.manualSelectionMode = true;
    }

    public reset(): void {
        this.physicalSampleChooserMode = false;
        this.manualSelectionMode = false;
        this.targetStudios = [];
        this.upgradeFirmwareInProgress = false;
        this.immediateFirmwareUpgrade = false;
        this.chosenSampleFirmwareRevision = new SampleFirmwareRevision({});
        this.chosenPhysicalSamples = [];
        this.eligiblePhysicalSamplesForUpgrade = [];
        this.eligibilityDiagnostics = null;
    }

    public confirmationMessage(): string {
        const sampleCount = this.chosenPhysicalSamples.length;
        const studioNames = this.manualSelectionMode ? this.selectedStudioNamesFromChosenSamples :
            this.targetStudioNames;
        return `This will upgrade ${ sampleCount } sample(s) in studio(s) '${ studioNames }' to firmware `
            + `'${ this.chosenSampleFirmwareRevision.releaseVersion }' `
            + `(${ this.immediateFirmwareUpgrade ? 'immediate release' : 'maintenance window release' }). Continue?`;
    }

    /** Always resets state after (success or failure); still rejects after the error toast so callers can react. */
    public submit(): Promise<void> {
        this.upgradeFirmwareInProgress = true;
        const sampleFirmwareUpgradeRequest = new SampleFirmwareUpgradeRequest();
        sampleFirmwareUpgradeRequest.firmwareRevisionId = this.chosenSampleFirmwareRevision.id;
        sampleFirmwareUpgradeRequest.firmwareFileName = this.chosenSampleFirmwareRevision.fileName;
        sampleFirmwareUpgradeRequest.targetReleaseVersion = this.chosenSampleFirmwareRevision.releaseVersion;
        sampleFirmwareUpgradeRequest.immediate = this.immediateFirmwareUpgrade;
        sampleFirmwareUpgradeRequest.studioTargets =
            buildStudioUpgradeTargets(this.targetStudios, this.chosenPhysicalSamples, !this.manualSelectionMode);

        return this.sampleFirmwareRevisionService.createSampleFirmwareUpgradeRequest(sampleFirmwareUpgradeRequest)
            .then(() => {
                this.messageService.add(
                    { severity: 'success', summary: 'Success', detail: 'Firmware Upgrade Request Submitted' });
            })
            .catch((error: any) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message });
                throw error;
            })
            .finally(() => {
                this.reset();
            });
    }

    /**
     * Loads the full eligible-sample set (across all pages), not just the currently visible page.
     *
     * @param preSelectAll Whether to pre-select every loaded sample once eligible samples arrive.
     */
    private loadEligiblePhysicalSamplesForUpgrade(preSelectAll: boolean): void {
        const targetStudios = this.targetStudios;
        const firmwareRevisionId = this.chosenSampleFirmwareRevision.id;
        this.loadingEligiblePhysicalSamples = true;
        this.eligibilityDiagnostics = null;
        Promise.all(
            targetStudios
                .filter(studio => studio.id != null)
                .map(studio => this.physicalSampleService.getEligiblePhysicalSamples(studio.id, firmwareRevisionId))
        ).then(results => {
            this.eligiblePhysicalSamplesForUpgrade = results.flat();
            if (preSelectAll) {
                this.chosenPhysicalSamples = [ ...this.eligiblePhysicalSamplesForUpgrade ];
            }
            if (this.eligiblePhysicalSamplesForUpgrade.length === 0) {
                this.loadEligibilityDiagnostics(targetStudios, firmwareRevisionId);
            }
        }).catch((error: any) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message });
        }).finally(() => {
            this.loadingEligiblePhysicalSamples = false;
        });
    }

    private loadEligibilityDiagnostics(targetStudios: Studio[], firmwareRevisionId: number): void {
        this.loadingEligibilityDiagnostics = true;
        Promise.all(
            targetStudios
                .filter(studio => studio.id != null)
                .map(studio => this.physicalSampleService.getEligibilityDiagnostics(studio.id, firmwareRevisionId))
        ).then(diagnosticsPerStudio => {
            this.eligibilityDiagnostics = new PhysicalSampleEligibilityDiagnostics({
                totalSamplesInStudio: diagnosticsPerStudio.reduce((sum, d) => sum + d.totalSamplesInStudio, 0),
                alreadyUpToDateCount: diagnosticsPerStudio.reduce((sum, d) => sum + d.alreadyUpToDateCount, 0),
                unmatchedPcbValues: Array.from(new Set(diagnosticsPerStudio.flatMap(d => d.unmatchedPcbValues)))
            });
        }).catch(() => {
            // Best-effort - the generic empty-state message is shown instead.
        }).finally(() => {
            this.loadingEligibilityDiagnostics = false;
        });
    }

}
